import { ReferencedEntry } from 'contentful';

import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { SalesChannelPricing } from 'src/api/contentful/contentTypes.shop';
import { getField } from 'src/utils/EntryUtil';
import { useProgramPromotion } from '../ProgramPromotion/ProgramPromotion';
import { useSalesChannel } from '../SalesChannel/SalesChannel';

const CLOVER_DEFAULT_PHONENUMBER = '+18442911950';

export interface PhoneNumberLink {
  url: string;
  linkText: string;
}

export interface ShopReseller {
  phoneNumber?: string;
  phoneNumberLink?: PhoneNumberLink;
}

export interface HasMinimumLabel {
  minimum?: string;
}

export interface Promotion {
  isFromUrl?: boolean;
}

export interface ShopTemplate {
  canShowPricing: boolean;
  hasPromotion: boolean;
  rates?: HasMinimumLabel;
  plans?: HasMinimumLabel;
  reseller?: ShopReseller;
  promotion?: Promotion;
  planSchedule?: Array<ReferencedEntry<SalesChannelPricing>>;
}

export const useShopTemplateContextInternal = (): ShopTemplate => {
  const { salesChannel, rateSchedule, planSchedule } = useSalesChannel();
  const { programPromotion } = useProgramPromotion();
  const { t } = useTranslation();

  const canShowPricing = !!(rateSchedule && rateSchedule.length > 0) || !!(planSchedule && planSchedule.length > 0);
  const hasPromotion = !!getField(salesChannel, 'currentPromotion') || !!programPromotion;
  const isFromUrl = !!programPromotion;

  const rates = useMemo(() => {
    const [ minimumRateSchedule ] = rateSchedule;
    if (!minimumRateSchedule) return;

    const minRate = getField(minimumRateSchedule, 'rate');
    if (minRate === undefined) return;

    const minRatePrice = getField(minimumRateSchedule, 'price');
    if (minRatePrice === undefined) {
      return {
        minimum: t('transaction-rate-no-fee', { rate: minRate }),
      };
    }

    return {
      minimum: t('transaction-rate-with-fee', {
        rate: minRate,
        fee: minRatePrice,
      }),
    };
  }, [ rateSchedule ]);

  const plans = useMemo(() => {
    const [ minimumPlanSchedule ] = planSchedule;
    if (!minimumPlanSchedule) return;

    return {
      minimum: t('product-price', { plan: getField(minimumPlanSchedule, 'price') }),
    };
  }, [ planSchedule ]);

  const reseller = useMemo(() => {
    const phoneNumber = getField(salesChannel, 'phoneNumber', CLOVER_DEFAULT_PHONENUMBER);
    if (!phoneNumber) return;

    const parsedPhoneNumber = parsePhoneNumberFromString(phoneNumber);
    if (!parsedPhoneNumber) return;

    return {
      phoneNumber: `[${parsedPhoneNumber.formatNational()}](${parsedPhoneNumber.getURI()})`,
      phoneNumberLink: { url: parsedPhoneNumber.getURI(), linkText: parsedPhoneNumber.formatNational() },
    };
  }, [ salesChannel ]);

  return {
    canShowPricing,
    promotion: {
      isFromUrl,
    },
    hasPromotion,
    rates,
    plans,
    reseller,
  };
};

export type ShopTemplateContext = ReturnType<typeof useShopTemplateContextInternal>;
